import { usePolicyTypes } from "@/lib/usePolicyTypes";
import {
  Car, Bike, Home, Heart, Stethoscope, Activity, Plane, Shield,
  Anchor, Briefcase, Building2, Truck, Tractor, Wrench, Sparkles,
  LifeBuoy, Scale, Crown, Gavel, ShieldCheck, HeartPulse,
} from "lucide-react";

// Mappa nome icona configurabile (Admin → Tipologie polizza) → componente lucide.
const ICON_MAP = {
  car: Car, bike: Bike, home: Home, heart: Heart, stethoscope: Stethoscope,
  activity: Activity, plane: Plane, shield: Shield, anchor: Anchor,
  briefcase: Briefcase, building2: Building2, truck: Truck, tractor: Tractor,
  wrench: Wrench, sparkles: Sparkles, lifebuoy: LifeBuoy, scale: Scale,
  crown: Crown, gavel: Gavel, shieldcheck: ShieldCheck, heartpulse: HeartPulse,
};

// Fallback "intelligente" per codice quando l'icona non è esplicitamente configurata
// (es. tipologie aggiunte dall'utente come "assistenza_stradale", "tutela_legale", ecc.).
const codeIconHeuristic = (code) => {
  const c = String(code || "").toLowerCase();
  if (!c) return Shield;
  if (/(strada|soccorso|assistenza|carro)/.test(c)) return LifeBuoy;
  if (/(legal|tutela|gavel)/.test(c)) return Scale;
  if (/(infortun)/.test(c)) return Activity;
  if (/(salute|sanit|medi)/.test(c)) return Stethoscope;
  if (/(vita|life)/.test(c)) return HeartPulse;
  if (/(casa|abitazion|home)/.test(c)) return Home;
  if (/(viaggio|travel)/.test(c)) return Plane;
  if (/(rc|responsabilita|liab)/.test(c)) return ShieldCheck;
  if (/(moto|scooter|ciclomotor)/.test(c)) return Bike;
  if (/(auto|car|veicolo)/.test(c)) return Car;
  if (/(autocarro|truck|furgone|commerc)/.test(c)) return Truck;
  if (/(agric|tractor)/.test(c)) return Tractor;
  if (/(nautic|barca|natant)/.test(c)) return Anchor;
  if (/(business|pmi|profession|impres|aziend)/.test(c)) return Briefcase;
  if (/(crown|premium|deluxe|gold)/.test(c)) return Crown;
  return Shield;
};

// Palette tailwind condivisa: chiavi allineate al backend (DEFAULT_POLICY_TYPES.color).
const COLOR_MAP = {
  blue: "bg-blue-50 border-blue-200 text-blue-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  rose: "bg-rose-50 border-rose-200 text-rose-700",
  teal: "bg-teal-50 border-teal-200 text-teal-700",
  orange: "bg-orange-50 border-orange-200 text-orange-700",
  sky: "bg-sky-50 border-sky-200 text-sky-700",
  indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
  violet: "bg-violet-50 border-violet-200 text-violet-700",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
  slate: "bg-slate-50 border-slate-200 text-slate-700",
  red: "bg-red-50 border-red-200 text-red-700",
  fuchsia: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700",
  lime: "bg-lime-50 border-lime-200 text-lime-700",
};

// Fallback colore per codice (se la tipologia non ne ha uno configurato).
const codeColorHeuristic = (code) => {
  const c = String(code || "").toLowerCase();
  if (/(auto|car)/.test(c)) return "blue";
  if (/(moto|scooter)/.test(c)) return "violet";
  if (/(casa|abitazion|home)/.test(c)) return "emerald";
  if (/(vita|life)/.test(c)) return "rose";
  if (/(salute|sanit)/.test(c)) return "teal";
  if (/(infortun)/.test(c)) return "orange";
  if (/(viaggio|travel)/.test(c)) return "sky";
  if (/(rc|responsabilita|liab)/.test(c)) return "indigo";
  if (/(strada|soccorso|assistenza)/.test(c)) return "amber";
  if (/(legal|tutela)/.test(c)) return "fuchsia";
  if (/(business|pmi|profession|impres|aziend|brief)/.test(c)) return "indigo";
  if (/(truck|furgone|commerc)/.test(c)) return "slate";
  if (/(agric|tractor)/.test(c)) return "lime";
  if (/(nautic|barca|natant)/.test(c)) return "sky";
  return "slate";
};

const SIZE_MAP = {
  xs: { wrap: "text-[10px] px-1.5 py-0.5 gap-1", icon: "h-3 w-3" },
  sm: { wrap: "text-[11px] px-2 py-0.5 gap-1.5", icon: "h-3 w-3" },
  md: { wrap: "text-xs px-2.5 py-1 gap-1.5", icon: "h-3.5 w-3.5" },
  lg: { wrap: "text-sm px-3 py-1 gap-2", icon: "h-4 w-4" },
};

/**
 * Badge uniforme per le tipologie di polizza (Auto, Moto, Casa, Assistenza stradale, ecc.).
 * Stile: pillola arrotondata con icona + label maiuscoletto, colori dalla configurazione
 *        (Admin → Tipologie polizza) e fallback "intelligente" per codici personalizzati.
 *
 * Props:
 *  - code       : codice tipologia (es. "auto", "assistenza_stradale")
 *  - policyTypes: (opzionale) array già caricato per evitare doppi fetch
 *  - size       : "xs" | "sm" | "md" | "lg" (default "sm")
 *  - className  : classi extra
 *  - showIcon   : default true
 *  - testid     : data-testid
 *  - forceLabel : override label
 *  - overrideIcon: nome icona override (es. "bike" per auto-moto detect)
 */
export function PolicyTypeBadge({
  code,
  policyTypes,
  size = "sm",
  className = "",
  showIcon = true,
  testid,
  forceLabel,
  overrideIcon,
}) {
  const { types: ctxTypes } = usePolicyTypes();
  const list = (policyTypes && policyTypes.length ? policyTypes : ctxTypes) || [];
  const cfg = list.find((t) => t.code === code) || null;
  const label = forceLabel || cfg?.label || (code ? String(code).replace(/_/g, " ") : "—");
  const iconKey = overrideIcon || cfg?.icon;
  const Icon = (iconKey && ICON_MAP[iconKey]) || codeIconHeuristic(code);
  const colorKey = cfg?.color || codeColorHeuristic(code);
  const colorCls = COLOR_MAP[colorKey] || COLOR_MAP.slate;
  const sz = SIZE_MAP[size] || SIZE_MAP.sm;

  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center font-semibold uppercase tracking-wide rounded-full border w-fit ${sz.wrap} ${colorCls} ${className}`}
      title={label}
    >
      {showIcon ? <Icon className={`${sz.icon} flex-shrink-0`} /> : null}
      <span className="truncate">{label}</span>
    </span>
  );
}

export default PolicyTypeBadge;
